// A dance's notation, made readable: each beat a chip with a tiny pictogram of
// its move, its manner, and a ×N count, under a heading that says it's a dance
// ("A dance · the Pip Spark · 10 beats"). The noticeboard, the Gazette and a
// dance's own page all draw it this way, so a newcomer always knows what the
// row of words is. No 3D here: the inline demo is loaded only when it's asked for.
import { picto } from './pictos.js';

export type Beat = { move: string; how?: string; times?: number; label?: string };
export type DanceRef = { name: string; title?: string; beats: (string | Beat)[] };

const esc = (s: any) => String(s ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]!));

/** "pebble-shuffle" -> "Pebble Shuffle"; a title of its own stays as it is. */
export function titleOf(d: { name: string; title?: string }) {
  const t = (d.title || '').trim();
  return t && t !== d.name ? t : d.name.split('-').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}
/** "the Tide Step"; a title that starts with a/an/the keeps its own article ("a free dance", never "the a free dance") */
const theTitle = (t: string) => (/^the\s/i.test(t) ? `the ${t.slice(4)}` : /^an?\s/i.test(t) ? t : `the ${t}`);
/** (P75) a dance's name as the chip by a dancer says it, "the Tide Step"; none for a free dance (P123) */
export function danceTitle(s: { dance?: string; title?: string }) {
  const name = (s.dance || '').trim(), t = (s.title || '').trim();
  if (!name || name === 'dance') return t && t !== 'dance' && !/^a free dance$/i.test(t) ? theTitle(t) : '';
  return theTitle(titleOf({ name, title: t }));
}

/** Beats as chips: plain move names (a run of the same move is one chip, ×N), or full beats, one chip each. */
export function chips(beats: (string | Beat)[]): { move: string; how?: string; times: number; label?: string }[] {
  const out: { move: string; how?: string; times: number; label?: string }[] = [];
  for (const b of beats) {
    if (typeof b === 'string') {
      const last = out[out.length - 1];
      if (last && last.move === b) last.times++; else out.push({ move: b, times: 1 });
    } else out.push({ move: b.move, how: b.how, times: Math.max(1, Math.min(4, b.times || 1)), label: b.label });
  }
  return out;
}
export const beatCount = (beats: (string | Beat)[]) => chips(beats).reduce((a, c) => a + c.times, 0);

export function scoreHtml(beats: (string | Beat)[]) {
  return `<ol class="score">${chips(beats).map((c, i) => {
    const words = c.move.replace(/-/g, ' ');
    const tip = c.label ? `${c.label} (${words})` : words + (c.how ? `, ${c.how}` : '');
    return `<li data-i="${i}" title="${esc(tip)}">${picto(c.move)}<b>${esc(words)}</b>${c.how ? ` <small>${esc(c.how)}</small>` : ''}${c.times > 1 ? `<i class="times">×${c.times}</i>` : ''}</li>`;
  }).join('')}</ol>`;
}

export function headingText(d: DanceRef) {
  const n = beatCount(d.beats);
  return `A dance · ${theTitle(titleOf(d))} · ${n} beat${n === 1 ? '' : 's'}`;
}

/** A dance card: the heading, the score, and the ways in. A frozen page (kept) gets a plain link instead of a demo. */
export function danceCard(d: DanceRef, o: { kept?: boolean; extra?: string } = {}) {
  const href = `/dance/${encodeURIComponent(d.name)}`;
  const n = beatCount(d.beats), title = titleOf(d);
  const head = `<div class="dance-head"><span class="dance-kick">A dance</span> · <b>${esc(theTitle(title))}</b> · ${n} beat${n === 1 ? '' : 's'}</div>`;
  if (o.kept) return `<a class="dance-card kept" href="${href}">${head}${scoreHtml(d.beats)}${o.extra || ''}<span class="dance-ways">See it danced →</span></a>`;
  return `<div class="dance-card" data-dance="${esc(d.name)}" tabindex="0" role="button" aria-expanded="false" aria-label="${esc(`${title}, a dance of ${n} beats: show it danced`)}">${head}${scoreHtml(d.beats)}${o.extra || ''}`
    + `<div class="dance-ways"><button type="button" class="see">▶ see it danced</button><a href="${href}">its page →</a></div></div>`;
}

export const SCORE_CSS = `
.score{list-style:none;display:flex;flex-wrap:wrap;gap:5px;margin:6px 0 6px;padding:0;text-indent:0}
.score li{display:inline-flex;align-items:center;gap:4px;border:1.5px solid #2a2219;border-radius:4px;padding:1px 7px 1px 3px;background:#fff;font:600 13px/1.3 ui-rounded,'SF Pro Rounded','Nunito',system-ui,sans-serif;color:#2a2219;transition:background .12s,transform .12s,box-shadow .12s}
.score li b{font-weight:600}
.score li small{color:#6b5a48;font-size:11.5px;font-weight:500}
.score li svg{width:20px;height:20px;flex:none}
.score li.now{background:#f6d77a;transform:translateY(-2px);box-shadow:0 4px 8px -4px rgba(0,0,0,.45)}
.score .times{display:inline-block;margin-left:2px;padding:0 5px 1px;border-radius:9px;font:800 11px/1.35 ui-rounded,'SF Pro Rounded',system-ui,sans-serif;font-style:normal;background:#8a4f8f;color:#fff}
.dance-card + .dance-card{margin-top:14px}
.dance-card{display:block;position:relative;color:inherit;text-decoration:none;cursor:pointer;border-radius:3px;outline-offset:3px}
.dance-card:focus-visible{outline:2.5px solid #2a63ad}
.dance-head{font:600 12.5px/1.3 ui-rounded,'SF Pro Rounded','Nunito',system-ui,sans-serif;color:#6b4a72;letter-spacing:.01em}
.dance-head b{color:#2a2219;font-weight:800}
.dance-kick{display:inline-block;padding:0 6px 1px;border-radius:9px;background:#8a4f8f;color:#fff;font-weight:800;font-size:11.5px}
.dance-ways{display:flex;flex-wrap:wrap;gap:6px 12px;align-items:center;margin-top:2px;font:600 12.5px ui-rounded,'SF Pro Rounded',system-ui,sans-serif}
.dance-ways a{color:#3f3570}
.dance-card.kept .dance-ways{color:#3f3570;text-decoration:underline}
.dance-ways .see{font:700 12.5px ui-rounded,'SF Pro Rounded',system-ui,sans-serif;color:#fffaf0;background:#8a4f8f;border:0;border-radius:999px;padding:4px 11px 5px;cursor:pointer;box-shadow:0 3px 6px -3px rgba(0,0,0,.5)}
.dance-ways .see:hover{background:#9c5fa1}
.dance-card.open .see{background:#2a2219}
.dance-stage{position:relative;width:100%;aspect-ratio:4/3;margin:6px 0 4px;border-radius:10px;overflow:hidden;background:linear-gradient(180deg,#f7eedc,#e7d2b2);box-shadow:inset 0 0 0 2px #fffdf7,0 8px 16px -10px rgba(0,0,0,.55);cursor:default}
.dance-stage canvas{display:block}
.dance-stage .stage-bar{position:absolute;left:6px;right:6px;bottom:6px;display:flex;gap:6px;justify-content:flex-end}
.dance-stage .stage-bar button{font:700 11.5px ui-rounded,'SF Pro Rounded',system-ui,sans-serif;color:#2a2219;background:rgba(255,250,240,.92);border:1.5px solid #2a2219;border-radius:999px;padding:2px 9px 3px;cursor:pointer}
.dance-stage .stage-bar button[aria-pressed=true]{background:#2a2219;color:#fffaf0}
`;
